/** The hour axis of an object's local day and a clock that ticks each minute. */
import { useEffect, useState } from 'react';
import { localDayLength, localDayStartUtc, positionToLocalLabel } from 'shared';

import type { DayAxis } from './Charts.js';

export function useNow(everyMs = 60_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), everyMs);
    return () => clearInterval(id);
  }, [everyMs]);
  return now;
}

/** Positions of `date` in `tz` (23 / 24 / 25), their wall-clock labels and «now» as a fractional position. */
export function dayAxis(date: string, tz: string, nowMs: number, nowLabel: string): DayAxis {
  const n = localDayLength(date, tz);
  const anchor = new Date(`${date}T12:00:00Z`);
  const labels = Array.from({ length: n + 1 }, (_, p) =>
    p < n ? positionToLocalLabel(anchor, tz, p) : '24:00',
  );
  const pos = (nowMs - localDayStartUtc(date, tz).getTime()) / 3_600_000;
  return { n, labels, now: pos >= 0 && pos <= n ? pos : null, nowLabel };
}

/** Wall-clock hour of a «HH:MM» label. */
export function hourOf(label: string | undefined): number {
  return Number((label ?? '00').slice(0, 2));
}

/**
 * Values of local day `from` (one per position) laid onto the positions of
 * another day by wall-clock hour, as the server lays an earlier day's prices
 * onto today (`schedule-builder.ts#byWallClock`): the first value of a
 * repeated hour, a missing hour takes its neighbour.
 */
export function byWallClock(
  values: readonly number[],
  from: string,
  tz: string,
  toLabels: readonly string[],
  n: number,
): number[] {
  const fromLabels = dayAxis(from, tz, 0, '').labels;
  const clock: (number | undefined)[] = new Array(24).fill(undefined);
  values.forEach((v, p) => {
    const hh = hourOf(fromLabels[p]);
    if (clock[hh] === undefined) clock[hh] = v;
  });
  for (let hh = 0; hh < 24; hh += 1) clock[hh] ??= clock[hh - 1] ?? clock[hh + 1] ?? 0;
  return Array.from({ length: n }, (_, p) => clock[hourOf(toLabels[p])] ?? 0);
}

/** 24 wall-clock cells laid onto the positions of a day. */
export function cellsOnDay(cells: readonly number[], labels: readonly string[], n: number): number[] {
  return Array.from({ length: n }, (_, p) => cells[hourOf(labels[p])] ?? 0);
}
